import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { MessageBanner, MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Modal } from "@amzn/stencil-react-components/modal";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import IconPlus from "@amzn/stencil-react-icons/icons/icon-plus";
import React, { FC } from "react";
import { FormProvider } from "react-hook-form";
import { AddressSuggestionsModal } from "@/common/components/AddressSuggestionsModal/address-suggestions-modal";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { ValidatedAddress } from "@/pages/Replacement/replacement.types";
import { formatSavedAddress } from "../address.mapper";
import type { AddressPrefix } from "../choose-delivery-form.types";
import { CHOOSE_DELIVERY_STRINGS } from "../choose-delivery.constants";
import type { AddressEditorState } from "./address-editor.types";
import { AddressFormFields } from "./address-form-fields";

interface SavedAddressProps {
  prefix: AddressPrefix;
  savedAddress: ValidatedAddress;
  onUseDifferentAddress: () => void;
}

const SavedAddress: FC<SavedAddressProps> = ({ prefix, savedAddress, onUseDifferentAddress }) => {
  const labelId = `${prefix}-saved-label`;
  return (
    <Col gridGap="S200" width="100%">
      <Text id={labelId} fontSize="T200" fontWeight="bold" color="neutral90">
        {getString(`${CHOOSE_DELIVERY_STRINGS}.savedAddressLabel`)}
      </Text>
      <Card width="100%" aria-labelledby={labelId} data-testid={`${prefix}-saved`}>
        <Text color="neutral90">{formatSavedAddress(savedAddress)}</Text>
      </Card>
      <Row>
        <Button
          variant={ButtonVariant.TertiaryInline}
          icon={<IconPlus aria-hidden />}
          onClick={onUseDifferentAddress}
          data-testid={`${prefix}-use-different`}
        >
          {getString(`${CHOOSE_DELIVERY_STRINGS}.useDifferentAddress`)}
        </Button>
      </Row>
    </Col>
  );
};

interface AddressFormProps {
  prefix: AddressPrefix;
  editor: AddressEditorState;
}

const AddressForm: FC<AddressFormProps> = ({ prefix, editor }) => (
  <Col gridGap="S300" width="100%">
    <AddressFormFields prefix={prefix} />
    {editor.error && (
      <View width="100%" role="alert" data-testid={`${prefix}-save-error`}>
        <MessageBanner type={MessageBannerType.Error} isDismissible={false}>
          {editor.error}
        </MessageBanner>
      </View>
    )}
    <Row gridGap="S300" justifyContent="flex-end" alignItems="center" width="100%">
      {editor.isSaving && (
        <Spinner
          size={SpinnerSize.Small}
          showText
          loadingText={getString(`${CHOOSE_DELIVERY_STRINGS}.validatingAddress`)}
        />
      )}
      {editor.savedAddress && (
        <Button
          variant={ButtonVariant.Secondary}
          onClick={editor.cancelEditing}
          disabled={editor.isSaving}
          data-testid={`${prefix}-cancel`}
        >
          {getString(`${CHOOSE_DELIVERY_STRINGS}.cancel`)}
        </Button>
      )}
      <Button
        variant={ButtonVariant.Primary}
        onClick={() => editor.saveAddress()}
        disabled={editor.isSaving}
        data-testid={`${prefix}-save`}
      >
        {getString(`${CHOOSE_DELIVERY_STRINGS}.saveAddress`)}
      </Button>
    </Row>
  </Col>
);

export interface AddressEditorProps {
  editor: AddressEditorState;
}

/**
 * The saved address with "Use a different address", or the form with Save address while one is being
 * entered. HOP's suggestion opens in a modal over either.
 */
export const AddressEditor: FC<AddressEditorProps> = ({ editor }) => (
  <FormProvider {...editor.form}>
    {!editor.isEditing && editor.savedAddress ? (
      <SavedAddress
        prefix={editor.prefix}
        savedAddress={editor.savedAddress}
        onUseDifferentAddress={editor.editNewAddress}
      />
    ) : (
      <AddressForm prefix={editor.prefix} editor={editor} />
    )}
    {editor.suggestion && (
      <Modal isOpen close={editor.closeSuggestion} isScrollable={false}>
        <AddressSuggestionsModal
          close={editor.closeSuggestion}
          mode={editor.suggestion.mode}
          enteredAddress={editor.suggestion.enteredAddress}
          suggestion={editor.suggestion.suggestion}
          handleValidAddress={editor.confirmSuggestion}
        />
      </Modal>
    )}
  </FormProvider>
);
